import {
  useGetFeatureArticleByIdQuery,
  useUpdateFeatureArticleMutation,
} from "@/app/features/FeatureArticle/featureArticleApiSlice";
import FormImageInput from "@/components/forms/FormImageInput";
import FormInput from "@/components/forms/FormInput";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(2).max(50).label("Name").trim(),
  redirectionUrl: Yup.string().required().min(1).label("redirectionUrl").trim(),
  image: Yup.mixed()
    .optional()
    .nullable(true)
    .test(
      "type",
      "Only the following formats are accepted: .jpg, .webp and.png",
      function (value) {
        if (!value) {
          return true;
        }
        return (
          value.type === "image/png" ||
          value.type === "image/jpg" ||
          value.type === "image/jpeg" ||
          value.type === "image/webp"
        );
      }
    )
    .label(""),
});

export default function ViewFeatureArticlePage() {
  const params = useParams();
  const { data, isLoading, isError, error } = useGetFeatureArticleByIdQuery(
    params.id
  );

  const [updatePopularSearch, { isLoading: isUpdating }] =
    useUpdateFeatureArticleMutation();

  const navigate = useNavigate();

  const handleSubmit = async (inputs) => {
    try {
      const formData = new FormData();
      formData.append("name", inputs.name);
      formData.append("redirectionUrl", inputs.redirectionUrl);

      if (inputs.image) {
        formData.append("image", inputs.image); // new image
      } else if (inputs.previewImage) {
        formData.append("image", inputs.previewImage); // send existing filename
      }

      const result = await updatePopularSearch({
        id: params.id,
        data: formData,
      });

      if (result && result.data) {
        toast.success(result.data.message);
        navigate("/feature-article");
      } else if (result && result.error) {
        toast.error(result.error.data.message);
      }
    } catch (error) {
      console.log("Error :", error);
      toast.error(error?.message || "Something went wrong");
    }
  };

  const popularSearch = data?.data?.data;

  if (isLoading) {
    return (
      <Skeleton
        sx={{
          transform: "scale(1, 1.0)",
          transformOrigin: "0% 0%",
        }}
        width="100%"
        height="600px"
      />
    );
  }

  if (isError) {
    return <p>{error?.data?.message || "Something went wrong"}</p>;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Edit Feature Article</Typography>
          </Stack>

          <Formik
            initialValues={{
              name: popularSearch?.name || "",
              redirectionUrl: popularSearch?.redirectionUrl || "",
              image: null,
              previewImage: popularSearch?.image || null,
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({
              setFieldValue,
              values,
              setFieldTouched,
              setFieldError,
              errors,
            }) => (
              <Form>
                <Stack spacing={3}>
                  <FormInput
                    label="Name"
                    name="name"
                    type="text"
                    placeholder="Enter Name"
                  />
                  <FormInput
                    label="redirectionUrl"
                    name="redirectionUrl"
                    type="text"
                    placeholder="Enter redirectionUrl"
                  />
                  <FormImageInput
                    name="image"
                    imageValues={values.previewImage}
                    onChangeSelectedImage={(event) => {
                      setFieldTouched(event.target.name);
                      setFieldValue(event.target.name, event.target.files[0]);
                    }}
                    onError={(message) => {
                      setFieldError("image", message);
                    }}
                    onCheckRes={(checkResolution) => {
                      setFieldValue("checkResolution", checkResolution);
                    }}
                    checkResolution={values.checkResolution}
                    label="Image"
                    imageSrcFolderPath="feature-article/"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdating}
                  >
                    Submit
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}
